import { useQuery } from "@tanstack/react-query";
import { AppLink, DrawerSection, errorText, Tile } from "@amber/ui";
import type { SourceValue } from "../../contracts/index";
import { sourceValueQuery } from "../queries";
import { Fact } from "./SourceSections";

const usd = (n: number) => `USD ${n < 1 ? n.toFixed(n === 0 ? 0 : 3) : n.toFixed(2)}`;
const STATE_LABELS: Record<string, string> = {
  not_obtained: "permission not obtained",
  unknown: "unknown",
  granted: "permission granted",
  licensed: "licensed",
  no_record: "no rights record",
  replaced_by_publisher: "replaced by the publisher",
};
const stateLabel = (state: string) => STATE_LABELS[state] ?? state.replace(/_/g, " ");

function growth(bytes: number): string {
  if (bytes >= 1_000_000_000) return `${(bytes / 1_000_000_000).toFixed(1)} GB`;
  return `${String(Math.max(bytes > 0 ? 1 : 0, Math.round(bytes / 1_000_000)))} MB`;
}

export function ValueSection({ sourceId }: { sourceId: string }) {
  const query = useQuery(sourceValueQuery(sourceId));
  const v = query.data;
  return (
    <DrawerSection title="Value and cost" note={`last ${String(v?.window_days ?? 30)} days`}>
      {query.isError ? <p className="m-0 text-md text-muted">{errorText(query.error)}</p> : null}
      {v ? (
        <>
          <div className="mb-2.5 grid grid-cols-3 gap-2">
            <Tile
              label="Eligible"
              value={String(v.eligible.total)}
              note={`${String(v.eligible.only_here)} only from here`}
            />
            <Tile
              label="Cost per eligible"
              value={v.cost.per_eligible_usd === null ? "—" : usd(v.cost.per_eligible_usd)}
              note={`${usd(v.cost.total_usd)} in total`}
            />
            <Tile
              label="Media"
              value={
                [
                  v.volume.media_hours > 0 ? `${v.volume.media_hours.toFixed(1)} h` : "",
                  v.volume.documents > 0 ? `${String(v.volume.documents)} documents` : "",
                ]
                  .filter(Boolean)
                  .join(" · ") || "—"
              }
              note={v.volume.media_hours > 0 ? "audio and video" : "text"}
            />
          </div>
          <ValueFacts v={v} />
          <p className="mt-2 mb-0 text-md text-muted">
            Cost includes this source’s share of shared costs, allocated once. Communications, not copies: one interview
            on three channels counts once.
          </p>
        </>
      ) : null}
    </DrawerSection>
  );
}

function ValueFacts({ v }: { v: SourceValue }) {
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-md">
      <Fact label="Eligible">
        {v.eligible.total > 0
          ? `${String(v.eligible.total)} communication${v.eligible.total === 1 ? "" : "s"}: ${String(v.eligible.only_here)} only from this source, ${String(v.eligible.also_elsewhere)} also from other sources`
          : `None in the last ${String(v.window_days)} days`}
      </Fact>
      <Fact label="Downloads">
        {`${String(v.downloads.succeeded)} succeeded`}
        {v.downloads.via_fallback > 0 ? ` · ${String(v.downloads.via_fallback)} via a fallback route` : ""}
        {v.downloads.failed > 0 ? (
          <>
            {" · "}
            <AppLink href={v.downloads.failed_href} className="text-link underline">
              {`${String(v.downloads.failed)} failed`}
            </AppLink>
          </>
        ) : null}
      </Fact>
      <Fact label="Human work">
        {v.human_work.corrections + v.human_work.decisions + v.human_work.tasks_waiting === 0
          ? "None"
          : `${String(v.human_work.corrections)} correction${v.human_work.corrections === 1 ? "" : "s"} · ${String(v.human_work.decisions)} decision${v.human_work.decisions === 1 ? "" : "s"}${v.human_work.tasks_waiting > 0 ? ` · ${String(v.human_work.tasks_waiting)} task${v.human_work.tasks_waiting === 1 ? "" : "s"} waiting` : ""}`}
      </Fact>
      <Fact label="Archive growth">{`+${growth(v.archive_growth_bytes)} of originals`}</Fact>
      <Fact label="Rights">
        {v.rights.length === 0
          ? "No captures"
          : v.rights.map((r) => `${String(r.count)} ${stateLabel(r.state)}`).join(" · ")}
      </Fact>
      <Fact label="Changes at origin">
        {v.origin_changes.length === 0
          ? "None seen"
          : v.origin_changes.map((r) => `${String(r.count)} ${stateLabel(r.state)}`).join(" · ")}
      </Fact>
    </dl>
  );
}
